import './App.css';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from './components/ErrorBoundary';
import SecurityWrapper from './components/SecurityWrapper';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import ReduxProvider from './components/ReduxProvider';
import Header from './components/Header';
import Main from './components/Main';
import MusicPlayer from './components/MusicPlayer';
import PrivacyPolicy from './components/PrivacyPolicy';
import { useAppState } from './hooks/useRedux';
import { initSounds } from './utils/soundManager';
import CursorTrail from './components/CursorTrail';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  },
});

const AppContent = () => {
  const { handleVerificationChange, app, ui } = useAppState();

  useEffect(() => {
    const cleanup = initSounds();
    return cleanup;
  }, []);

  return (
    <Router>
      <div>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route
            path="/*"
            element={
              <div>
                <Header
                  isVerified={app.isVerified}
                  showNavbar={ui.showNavbar}
                />
                <Main onVerificationChange={handleVerificationChange} />
                <MusicPlayer show={ui.showNavbar} />
              </div>
            }
          />
        </Routes>
      </div>
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <SecurityWrapper>
        <CursorTrail />
        <PerformanceOptimizer />
        <QueryClientProvider client={queryClient}>
          <ReduxProvider>
            <AppContent />
          </ReduxProvider>
        </QueryClientProvider>
      </SecurityWrapper>
    </ErrorBoundary>
  );
}

export default App;

// Global error trap — writes crash details directly into the DOM so they're
// visible even when the browser console isn't open.
window.onerror = (msg, src, line, col, err) => {
  const el = document.getElementById('root');
  if (el && !el.innerHTML.trim()) {
    el.innerHTML = `<div style="background:#111;color:#f88;padding:20px;font-family:monospace;font-size:14px;white-space:pre-wrap">
<b>JS crash — please send this to abel:</b>

${msg}
${src}:${line}:${col}
${err ? err.stack : ''}
</div>`;
  }
};

window.onunhandledrejection = e => {
  const el = document.getElementById('root');
  if (el && !el.innerHTML.trim()) {
    el.innerHTML = `<div style="background:#111;color:#fa0;padding:20px;font-family:monospace;font-size:14px;white-space:pre-wrap">
<b>Unhandled Promise rejection:</b>

${e.reason}
</div>`;
  }
};
